/**
 * Copyright 2025 (c) Christian von Arnim, ISW University of Stuttgart (for umati and VDW e.V.)
 */
import { ExternalNodeReference, NodeIdentifier, ns0, PubSubAddressSpace, UaNode } from './PubSubAddressSpace';
import { CompareNodeId, CompareQualifiedName, NodeId, QualifiedName } from './PubSubTypes';

export type PubSubNodeFilter = (node: UaNode) => UaNode[];
export type PubSubNodeFilterFactory = (addrSpace: PubSubAddressSpace) => PubSubNodeFilter;

export function FilterBrowsename(browseName: QualifiedName): PubSubNodeFilterFactory {
  return () => (node: UaNode) => {
    let ret: UaNode[] = [];

    for (const ref of node.References) {
      if (ref.isForward && CompareQualifiedName(ref.TargetNode.BrowseName, browseName)) {
        ret.push(ref.TargetNode);
      }
    }

    return ret;
  };
}

// Returns the current node unchanged. Used for variablePath ".".
export function FilterCurrentNode(): PubSubNodeFilterFactory {
  return () => (node: UaNode) => [node];
}

export function FilterReferenceAndTypeId(referenceId: NodeId, typeId: NodeId): PubSubNodeFilterFactory {
  const hasTypeDefinitonNodeId: NodeId = { Id: ns0.HasTypeDefinition };
  const correctTypeId = (node: UaNode) => {
    const typeReference = node.ExternalReferences.find(
      (extRef: ExternalNodeReference) =>
        CompareNodeId(extRef.ReferenceTypeId, hasTypeDefinitonNodeId) && CompareNodeId(extRef.TargetNode, typeId),
    );
    return typeReference !== undefined;
  };

  return () => {
    return (node: UaNode) => {
      let ret: UaNode[] = [];
      for (const ref of node.References) {
        if (ref.isForward && CompareNodeId(ref.ReferenceTypeId, referenceId) && correctTypeId(ref.TargetNode)) {
          ret.push(ref.TargetNode);
        }
      }
      return ret;
    };
  };
}

// Return a start node with a specific Id
export function FilterStartNode(nodeId: NodeIdentifier | undefined): PubSubNodeFilterFactory {
  return (adSpace: PubSubAddressSpace) => {
    let nodes: UaNode[] = [];
    if (nodeId !== undefined) {
      const reqNode = adSpace.getState().getNodeById(nodeId);
      if (reqNode !== undefined) {
        nodes.push(reqNode);
      }
    }
    return () => {
      return nodes;
    };
  };
}
